import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Rss } from "lucide-react"

import { Pagination } from "@/components/changelog/pagination"
import { PublicEntryCard } from "@/components/changelog/public-entry-card"
import { PublicHeader } from "@/components/changelog/public-header"
import { SubscribeForm } from "@/components/changelog/subscribe-form"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { publicListEntries } from "@/http/changelog/public"
import { env } from "@/lib/env"

const publicEntriesQueryOptions = (
  serviceSlug: string,
  page: number,
) =>
  queryOptions({
    queryKey: ["public-changelog", serviceSlug, page],
    queryFn: () => publicListEntries(serviceSlug, page),
  })

type TSearchParams = {
  page?: number
}

export const Route = createFileRoute(
  "/changelog/$serviceSlug/",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): TSearchParams => ({
    page: Math.max(1, Math.floor(Number(search.page) || 1)),
  }),
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    await context.queryClient.ensureQueryData(
      publicEntriesQueryOptions(params.serviceSlug, deps.page),
    )
  },
  component: PublicChangelogPage,
})

function PublicChangelogPage() {
  const { serviceSlug } = Route.useParams()
  const { page = 1 } = Route.useSearch()
  const navigate = Route.useNavigate()

  const { data } = useSuspenseQuery(
    publicEntriesQueryOptions(serviceSlug, page),
  )

  const entries = data?.entries ?? []
  const totalPages = data?.totalPages ?? 1
  const service = data?.service

  const handlePageChange = (newPage: number) => {
    navigate({
      search: { page: newPage },
    })
  }

  const rssUrl = `${env.apiUrl.replace(/\/$/, "")}/changelog/public/${serviceSlug}/rss`

  return (
    <div className="min-h-svh">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12 lg:py-16">
        {service && (
          <PublicHeader service={service}>
            <SubscribeForm serviceSlug={serviceSlug} />
            <Button variant="ghost" size="sm" asChild>
              <a
                href={rssUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Rss className="mr-1.5 size-4" />
                RSS Feed
              </a>
            </Button>
          </PublicHeader>
        )}

        <div className="mt-10 sm:mt-12">
          {entries.length === 0 && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              Nenhuma atualização publicada ainda.
            </p>
          )}

          {entries.map((entry, i) => (
            <div key={entry.id}>
              {i > 0 && <Separator className="my-8" />}
              <PublicEntryCard
                entry={entry}
                serviceSlug={serviceSlug}
              />
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-12">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      <footer className="border-t py-6 text-center">
        <p className="text-muted-foreground/60 text-xs">
          Powered by HEROw
        </p>
      </footer>
    </div>
  )
}
